import { SCENARIO_MAP, type Scenario } from "./scenario";
import type { AssignmentReason } from "./teamRouting";
import { HOURS } from "./window";

/**
 * TA-1361: the one reading of "may an automated nurture touch reach this
 * thread", for every path that writes to a customer who did not just write to
 * us: the follow-up nudge (`planFollowUp`, followUp.ts), a campaign wave
 * (`decideCampaignWave`, campaign.ts), a Shepherd reactivation touch
 * (`planShepherdProposals`, shepherdPlan.ts) and an appointment reminder
 * (packages/agents/src/appointmentReminderSweep.ts).
 *
 * Before this, only the review ask read an open complaint (decision list item
 * 124, reviewSweep.ts and reviewCycleHandler.ts), and no nurture path read the
 * thread's route. So a customer who complained (routed `complaint_warranty`,
 * class D), was answered by hand and went quiet for three days was sent the
 * check-in nudge on the complaint thread. CLAUDE.md section 3 makes class D
 * rows human-only and B13 says when unsure, don't: this is the conservative
 * reading, logged by the controller under Track A.
 *
 * TWO ARMS, either one holds:
 *
 * 1. `open_complaint`: the contact holds an open complaint, read by the one
 *    reader the review ask already uses (`readOpenComplaints`,
 *    packages/db/src/complaintClose.ts), so the two can never disagree about
 *    what is open. It lifts when an operator's `complaint.closed` names every
 *    open complaint message.
 * 2. `class_d_thread`: the thread's latest `routing.decided` row routed a
 *    class D scenario, read off core's own map (`SCENARIO_MAP`), never a list
 *    written here. It lifts when a later customer message routes to a row that
 *    is not class D, or when a `complaint.closed` names the message that row
 *    routed. A scenario the map does not know reads as `unknown`, which is
 *    class D (scenario.ts: an unrecognised message goes to a human).
 *
 * 3. `human_takeover` (TA-1371 C4, built switched off behind
 *    `ACKRA_ENABLE_TAKEOVER_HOLD`): a person on the shop's team took the
 *    contact, by typing to them in the inbox (a `sender = 'shop'` message on
 *    any of the contact's threads) or by taking or being handed one of their
 *    threads (an open `thread_assignments` row whose reason is a person's,
 *    `HUMAN_TAKEOVER_ASSIGNMENT_REASONS`). It holds for
 *    `HUMAN_TAKEOVER_HOLD_MS` from the later of the two, then lifts on its
 *    own. Named last: the other two are lifted only by a person, so the row
 *    says what would lift it. With the flag off the caller passes no takeover
 *    facts and the answer is exactly the two-arm answer (section 11: a
 *    behaviour change waits for Kabir's typed go).
 *
 * Reply drafts are untouched: they are human-gated, and F-06 is measured on
 * them. A held candidate is never a silent skip: each caller files its own
 * restraint row with the reason code (B3, F-21).
 */

export const NURTURE_HOLD_REASONS = ["open_complaint", "class_d_thread", "human_takeover"] as const;
export type NurtureHoldReason = (typeof NURTURE_HOLD_REASONS)[number];

/** True for a scenario whose row in core's map is class D, and for one the map does not know (read as `unknown`). */
export function isClassDScenario(scenario: string | null): boolean {
  if (scenario === null || !Object.hasOwn(SCENARIO_MAP, scenario)) return true;
  return SCENARIO_MAP[scenario as Scenario].graduationClass === "D";
}

export interface NurtureHoldInput {
  /**
   * The thread's latest `routing.decided` row, or null when nothing on it was
   * routed. `scenario` is null when the row carries none. `closedByComplaintClose`
   * is true when a `complaint.closed` event on the thread names the message
   * that row routed.
   */
  readonly latestRoute: { readonly scenario: string | null; readonly closedByComplaintClose: boolean } | null;
  /** The contact holds an open complaint (`readOpenComplaints`). */
  readonly hasOpenComplaint: boolean;
  /**
   * TA-1371 C4: the takeover clocks of the thread's contact, read by the
   * caller only while `ACKRA_ENABLE_TAKEOVER_HOLD` is on. Absent or null is the
   * flag off: no takeover arm, the answer exactly as before it existed.
   */
  readonly humanTakeover?: HumanTakeoverInput | null;
}

/**
 * TA-1371 C4 (CLAUDE.md sections 3 and 7, B13): how long a person's takeover
 * holds every scheduled play (the follow-up nudge, a campaign wave, a Shepherd
 * touch, the review ask, and the appointment reminder unless Kabir exempts
 * it), counted from the later of the owner's last typed message and the take.
 *
 * AWAITING RATIFICATION. 72 hours is the implementer's conservative reading,
 * chosen to match `FOLLOWUP_SILENCE_MS` (followUp.ts), not a number Kabir or
 * Mike has seen. It is a suppression timer of the kind section 3 makes
 * human-only (T3: "Guardrails, escalation triggers, SLA timers"), so only
 * Kabir changes it, and the flag that turns the arm on waits for his typed go.
 */
export const HUMAN_TAKEOVER_HOLD_MS = 72 * HOURS;

/**
 * The `thread_assignments` reasons a person writes (teamOps.ts: `taken` is a
 * person taking an unassigned thread, `manual` the owner assigning it,
 * `handoff` one person handing it to another with a note). The router's own
 * reasons (`round_robin`, `expertise`, `vip_owner` and the two unassigned
 * ones) assign every new thread and say nothing about a person stepping in,
 * so they never count.
 */
export const HUMAN_TAKEOVER_ASSIGNMENT_REASONS = ["taken", "manual", "handoff"] as const satisfies readonly AssignmentReason[];

export interface HumanTakeoverInput {
  /** The latest `sender = 'shop'` message on any of the contact's threads, or null. */
  readonly lastOwnerTypedAt: Date | null;
  /** The latest start of an open person-written assignment on any of the contact's threads, or null. */
  readonly takenAt: Date | null;
  readonly now: Date;
}

function assertValid(d: Date | null, what: string): void {
  if (d !== null && Number.isNaN(d.getTime())) throw new Error(`humanTakeoverHolds: ${what} is an invalid date`);
}

/**
 * True while a person's takeover holds: the later of the two clocks is less
 * than `HUMAN_TAKEOVER_HOLD_MS` before `now`. At exactly the hold it has
 * lifted. A clock ahead of `now` holds (skew fails toward silence, as
 * `planFollowUp`'s `clock_skew` does). An invalid date throws: read as no
 * hold it would let a play through on a broken read (section 0).
 */
export function humanTakeoverHolds(input: HumanTakeoverInput): boolean {
  assertValid(input.lastOwnerTypedAt, "lastOwnerTypedAt");
  assertValid(input.takenAt, "takenAt");
  assertValid(input.now, "now");
  const clocks = [input.lastOwnerTypedAt, input.takenAt].filter((d): d is Date => d !== null).map((d) => d.getTime());
  if (clocks.length === 0) return false;
  return input.now.getTime() - Math.max(...clocks) < HUMAN_TAKEOVER_HOLD_MS;
}

/**
 * The hold on this thread, or null when a nurture touch may go on to its own
 * checks. An open complaint is named first: it is the one an operator can
 * close (`pnpm complaints close`), so the row says what would lift it. A
 * takeover is named last: it lifts on its own clock.
 */
export function nurtureHoldFor(input: NurtureHoldInput): NurtureHoldReason | null {
  if (input.hasOpenComplaint) return "open_complaint";
  const route = input.latestRoute;
  if (route !== null && !route.closedByComplaintClose && isClassDScenario(route.scenario)) return "class_d_thread";
  if (input.humanTakeover !== undefined && input.humanTakeover !== null && humanTakeoverHolds(input.humanTakeover)) {
    return "human_takeover";
  }
  return null;
}
